import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { FaCheckCircle } from "react-icons/fa";
import { getAccessToken } from "../../storage/storage";
import { FormatDate } from "../../utils/FormateDate";
import CommentModal from "../Dashboard/components/CommentModal";

// TableSkelton (components/Skeletons.jsx) hardcodes 9 columns, tailored to
// the wider stays tables — this table only has 6, so a scoped skeleton
// avoids misaligned ghost columns.
const INVOICE_TABLE_COLUMNS = 6;
const InvoiceTableSkeleton = ({ rows = 6 }) => (
  <>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <tr key={rowIndex} role="status" className="animate-pulse border-t border-t-[#DFDFDF]">
        {Array.from({ length: INVOICE_TABLE_COLUMNS }).map((_, colIndex) => (
          <td key={colIndex} className="py-5 px-2">
            <div className="flex justify-center items-center">
              <div className="w-20 h-3 bg-gray-200 rounded-full"></div>
            </div>
          </td>
        ))}
      </tr>
    ))}
  </>
);

// Reuses the exact same endpoint Dashboard.jsx's "Invoices for Approval"
// widget already calls — GET /api/user/properties/invoice/ is hardcoded
// server-side to status="pending", so this page's whole purpose (invoices
// awaiting approval) is always what it returns; there's no separate
// "unfiltered" mode to toggle back to, unlike the Upcoming Stays page.
export default function InvoicesForApproval() {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Guards against the same stale-response race condition fixed on the
  // Upcoming Stays page — cancels a still-in-flight request before issuing
  // a new one so an older response can never overwrite a newer result.
  const requestRef = useRef(null);

  // Keep ?filter=pending-approval in the URL so this page stays bookmarkable
  // /shareable, per the ticket's AC. There's no toggle to turn it off on this
  // page — "Clear Filter" navigates back to the dashboard instead.
  useEffect(() => {
    if (searchParams.get("filter") !== "pending-approval") {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("filter", "pending-approval");
          return next;
        },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvoices = useCallback(() => {
    if (requestRef.current) {
      requestRef.current.abort();
    }
    const controller = new AbortController();
    requestRef.current = controller;

    setLoading(true);
    axios
      .get(`${BASE_URL}/api/user/properties/invoice/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal: controller.signal,
      })
      .then((response) => {
        const properties = Array.isArray(response.data) ? response.data : [];
        const flattened = [];
        properties.forEach((property) => {
          (property.pending_invoices || []).forEach((invoice) => {
            flattened.push({
              id: invoice.id,
              invoiceNumber: invoice.invoice_number,
              cleanerName: invoice.cleaner
                ? `${invoice.cleaner.first_name} ${invoice.cleaner.surname}`
                : "—",
              propertyName: invoice.property || property.name,
              stayReference: invoice.guest_stay,
              amount: invoice.total_amount,
              invoiceDate: invoice.created_at,
            });
          });
        });
        setInvoices(flattened);
      })
      .catch((error) => {
        if (axios.isCancel(error) || error.name === "CanceledError") return;
        console.error("Error fetching invoices:", error);
      })
      .finally(() => {
        if (requestRef.current === controller) {
          setLoading(false);
        }
      });
  }, [BASE_URL, token]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleApprove = async (invoiceId) => {
    try {
      const response = await axios.put(
        `${BASE_URL}/api/invoices/${invoiceId}/approve/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      // Remove from the filtered view in real time.
      setInvoices((prev) => prev.filter((inv) => inv.id !== invoiceId));
      Swal.fire({
        icon: "success",
        title: response?.data?.message || "Invoice Approved",
        timer: 2000,
        showConfirmButton: false,
        position: "center",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: error.response?.data?.detail || "Something went wrong!",
        timer: 2000,
        showConfirmButton: false,
        position: "center",
      });
    }
  };

  const handleRejectClick = (invoice) => {
    setSelectedInvoice(invoice);
    setIsOpenModal(true);
  };

  const handleRejectConfirm = (invoiceId) => {
    setInvoices((prev) => prev.filter((inv) => inv.id !== invoiceId));
    setIsOpenModal(false);
    setSelectedInvoice(null);
  };

  return (
    <div className="flex flex-col px-6 w-full h-full pb-8">
      <h1 className="md:text-3xl text-lg font-bold text-[#586989] mt-8 mb-2">
        Invoices for Approval
      </h1>
      <p className="text-gray-600 mb-4">
        Review and action outstanding cleaner invoices
      </p>

      <div className="border border-[#DFDFDF] bg-white rounded-lg shadow-md overflow-auto mt-4">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 bg-[#FFF5CE] text-[#586989] z-10">
            <tr>
              <th className="p-3 text-center min-w-[160px]">Property Name</th>
              <th className="p-3 text-center min-w-[120px]">Stay Reference</th>
              <th className="p-3 text-center min-w-[160px]">Cleaner Name</th>
              <th className="p-3 text-center min-w-[140px]">Invoice Date</th>
              <th className="p-3 text-center min-w-[100px]">Amount</th>
              <th className="p-3 text-center min-w-[180px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <InvoiceTableSkeleton />
            ) : invoices.length > 0 ? (
              invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="border-b hover:bg-gray-50 transition-colors"
                >
                  <td className="p-3 text-center">{invoice.propertyName}</td>
                  <td className="p-3 text-center">
                    {invoice.stayReference ? `#${invoice.stayReference}` : "-"}
                  </td>
                  <td className="p-3 text-center">{invoice.cleanerName}</td>
                  <td className="p-3 text-center">
                    {invoice.invoiceDate ? FormatDate(invoice.invoiceDate) : "-"}
                  </td>
                  <td className="p-3 text-center">£{invoice.amount}</td>
                  <td className="p-3 text-center">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => handleApprove(invoice.id)}
                        className="px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleRejectClick(invoice)}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200 transition-colors"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center py-16">
                  <div className="flex flex-col items-center gap-3">
                    <FaCheckCircle className="text-4xl text-green-500" />
                    <p className="text-lg font-bold text-[#586989]">
                      All invoices have been reviewed.
                    </p>
                    <button
                      onClick={() => navigate("/dashboard")}
                      className="mt-2 bg-[#586989] text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-[#47546d] transition-colors"
                    >
                      Back to Dashboard
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CommentModal
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        invoiceId={selectedInvoice?.id}
        onRejectConfirm={handleRejectConfirm}
      />
    </div>
  );
}
